import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { usePawnMode } from "../../Context/PawnModeContext";
// Rounded variants throughout — the house icon style for the whole app.
import {
  MenuRounded as MenuIcon,
  DashboardRounded as DashboardIcon,
  PeopleRounded as CustomersIcon,
  ReceiptRounded as TicketIcon,
  ReceiptLongRounded as ReceiptLongIcon,
  TaskAltRounded as SettlementIcon,
  PaymentRounded as PaymentIcon,
  AssessmentRounded as ReportIcon,
  SettingsRounded as SettingsIcon,
  ChevronLeftRounded as ChevronLeftIcon,
  StorefrontRounded as StoreIcon,
  LogoutRounded as LogoutIcon,
} from "@mui/icons-material";
import AssuredWorkloadRoundedIcon from "@mui/icons-material/AssuredWorkloadRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import { ReactComponent as DailyTickIcon } from "../../assets/icons/loading.svg";
import DiamondRoundedIcon from "@mui/icons-material/DiamondRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import ImportContactsRoundedIcon from "@mui/icons-material/ImportContactsRounded";
import CloudDownloadRoundedIcon from "@mui/icons-material/CloudDownloadRounded";
import { StoreSwitcherDialog, useSwitchableStores } from "../StoreSwitcher";
import {
  BranchSwitcherDialog,
  useSwitchableBranches,
} from "../BranchSwitcher";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import LogoutConfirmDialog from "../LogoutConfirmDialog";
import { isPawnStore } from "../../utils/moduleNav";

// White + blue theme tokens (mirrors the prototype).
const SIDEBAR_BG = "#F8FAFC"; // slate-50
const PAGE_BG = "#FFFFFF";
const BORDER = "#E2E8F0"; // slate-200
const BLUE = "#2563EB"; // blue-600
const BLUE_PALE = "#EFF6FF"; // active bg
const BLUE_INK = "#1E40AF"; // active text
const INK = "#0F172A"; // slate-900
const INK_SOFT = "#334155"; // slate-700
const INK_MUTED = "#64748B"; // slate-500
const INK_FAINT = "#94A3B8"; // slate-400

const PawnLayout = () => {
  const currentStoreInfo =
    JSON.parse(localStorage.getItem("CapacitorStorage.current_store_info")) ||
    {};

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  /* `noSsr` on both: without it the first render always answers `false`, so a
   * desktop would paint one frame of the phone layout — and, worse, the
   * touch-only menu button — before correcting itself. */
  const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

  /* Hover-expand is only a real interaction on a device with a real pointer.
   * Touch browsers synthesise a `mouseenter` on tap but never a matching
   * `mouseleave`, which used to leave the rail expanded over the content with
   * no way to shut it. So: fine pointers hover, coarse pointers tap. */
  const canHover = useMediaQuery("(hover: hover) and (pointer: fine)", {
    noSsr: true,
  });

  const [isHovered, setIsHovered] = useState(false); // fine-pointer hover
  const [railOpen, setRailOpen] = useState(false); // coarse-pointer tap toggle
  const [drawerOpen, setDrawerOpen] = useState(false); // phone temporary drawer

  /* ── Store switcher ─────────────────────────────────────────────────────────
   * These screens are exclusive to is_pawn stores, so the rail's switcher offers
   * pawn stores ONLY — moving between two pawn counters is a move within this
   * module and never leaves it. The full group (travel and plain stores included)
   * is offered by the Daily Tick sidebar instead, which is the one place where
   * changing module is the point. Nothing is rendered here when this store has no
   * other pawn store beside it. */
  const [switcherOpen, setSwitcherOpen] = useState(false);
  /* Signing out. A pawn store now opens straight into this module at login, so
   * these screens can be a whole session's worth of app — the way out has to be
   * here rather than only in the Daily Tick sidebar. It sits in the pinned
   * footer beside the Daily Tick button, which is the one part of the rail that
   * never scrolls away. */
  const [logoutOpen, setLogoutOpen] = useState(false);
  const { stores: connectedStores, loading: switchableLoading } =
    useSwitchableStores();
  const switchableStores = useMemo(
    () => connectedStores.filter(isPawnStore),
    [connectedStores],
  );

  // The branch picker needs to be here for the same reason the store picker
  // is: these screens can be a whole session, and a pawn shop with two counters
  // is exactly the case branches were added for. Not filtered like the store
  // list above — every branch belongs to the store already open, so there is no
  // other module to stray into.
  const [branchSwitcherOpen, setBranchSwitcherOpen] = useState(false);
  const {
    branches: switchableBranches,
    loading: branchesLoading,
    canSwitch: canSwitchBranches,
    canViewAll: canViewAllBranches,
    viewingAll: viewingAllBranches,
  } = useSwitchableBranches();

  // The persistent rail is expanded by hover on a mouse, by an explicit tap on
  // a touch tablet. `drawerOpen` belongs to the phone drawer alone and is
  // deliberately NOT part of this: conflating the two meant that rotating a
  // tablet from portrait (drawer open) to landscape left the rail pinned open
  // with nothing left on screen able to close it.
  const isExpanded = !isMobile && (canHover ? isHovered : railOpen);

  // Any breakpoint / pointer-capability change starts from a closed sidebar, so
  // a state that only made sense in the previous layout can't survive into the
  // next one (rotate a tablet, dock a keyboard, resize a window).
  useEffect(() => {
    setIsHovered(false);
    setRailOpen(false);
    setDrawerOpen(false);
  }, [isMobile, canHover]);

  /* ── Pawn official/unofficial mode gesture (touch) ──────────────────────────
   * Press-and-hold for 3s:
   *   Official   → password prompt; entering "rs"/"RS" switches to Unofficial.
   *   Unofficial → reverts straight back to Official (mirrors the desktop F12).
   * A completed long-press is swallowed so it doesn't also open the drawer.
   *
   * The gesture lives on TWO targets so every form factor can reach it:
   *   · the phone AppBar's menu icon, and
   *   · the store-logo tile, which is present in the collapsed rail, the
   *     expanded rail and the phone drawer header alike — the only element that
   *     exists at every width. Tablets have no menu icon (no AppBar), so
   *     without the logo target they had no way in at all.                    */
  const OFFICIAL = 1;
  const UNOFFICIAL = 2;
  const MODE_SECRET = "rs";
  const HOLD_MS = 3000;
  const { mode, isUnofficial, setMode } = usePawnMode();
  const holdTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwValue, setPwValue] = useState("");
  const [pwError, setPwError] = useState(false);

  const triggerModeGesture = () => {
    if (mode === UNOFFICIAL) {
      setMode(OFFICIAL);
    } else {
      setPwValue("");
      setPwError(false);
      setPwOpen(true);
    }
  };

  const startHold = () => {
    longPressFiredRef.current = false;
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      triggerModeGesture();
    }, HOLD_MS);
  };

  const cancelHold = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  // Pointer handlers to spread onto any element that should arm the gesture.
  const holdProps = {
    onPointerDown: startHold,
    onPointerUp: cancelHold,
    onPointerLeave: cancelHold,
    onPointerCancel: cancelHold,
    onContextMenu: (e) => e.preventDefault(),
  };

  // A trailing click that belongs to a completed 3s hold must not also run the
  // target's normal action. Returns true when the click was part of the gesture.
  const swallowedByHold = () => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return true;
    }
    return false;
  };

  const handleMenuClick = () => {
    if (swallowedByHold()) return;
    toggleDrawer();
  };

  // The logo tile carries the hold gesture only — a plain tap does nothing, so
  // the tile stays as innocuous as it looks. Opening the rail on a touch tablet
  // is the job of the visible menu button beside it.
  const handleLogoClick = () => {
    swallowedByHold();
  };

  const submitPassword = () => {
    if (pwValue.trim().toLowerCase() === MODE_SECRET) {
      setMode(UNOFFICIAL);
      setPwOpen(false);
      setPwValue("");
      setPwError(false);
    } else {
      setPwError(true);
    }
  };

  // Grouped navigation. "Operations" = daily counter work; "Masters & Settings" = config.
  //
  // "Learn" sits FIRST, above the dashboard, on purpose: a walkthrough is only
  // worth anything to someone who doesn't know the app yet, and that person is
  // looking at a collapsed icon rail with no idea what any of it does. Putting it
  // last would hide it from exactly the reader it was written for.
  const menuSections = [
    {
      heading: "Learn & Import",
      items: [
        {
          label: "Tutorial",
          path: "/pawn/tutorial",
          icon: <SchoolRoundedIcon />,
        },
        {
          label: "Import Data",
          path: "/pawn/import-data",
          icon: <CloudDownloadRoundedIcon />,
        },
      ],
    },
    {
      heading: "Operations",
      items: [
        { label: "Dashboard", path: "/pawn/home", icon: <DashboardIcon /> },
        {
          label: "Payment Screen",
          path: "/pawn/payment",
          icon: <PaymentIcon />,
        },

        {
          label: "Receipt Screen",
          path: "/pawn/receipt",
          icon: <ReceiptLongIcon />,
        },
        {
          label: "Settlement Screen",
          path: "/pawn/settlement",
          icon: <SettlementIcon />,
        },
      ],
    },
    {
      heading: "Reports",
      items: [
        {
          label: "Reports",
          path: "/pawn/reports",
          icon: <ReportIcon />,
        },
      ],
    },
    {
      heading: "Masters",
      items: [
        {
          label: "Customer",
          path: "/pawn/customer-master",
          icon: <CustomersIcon />,
        },
        {
          label: "Metal, Item Type & Floor",
          path: "/pawn/masters",
          icon: <DiamondRoundedIcon />,
        },
        {
          label: "Ticket Studio",
          path: "/pawn/ticket-generator",
          icon: <TicketIcon />,
        },
        {
          label: "Barcode Mapper",
          path: "/pawn/barcode-mapper",
          icon: <QrCodeScannerRoundedIcon />,
        },
      ],
    },
    {
      heading: "Accounts & Ledger",
      // The pawn-mounted twins of the shared Cloud Accounting screens (see the
      // /pawn/accounts* routes in App.js) — same components, pawn chrome kept.
      items: [
        {
          label: "Account Master",
          path: "/pawn/accounts",
          icon: <AssuredWorkloadRoundedIcon />,
        },
        {
          label: "Ledger",
          path: "/pawn/accounts-ledger",
          icon: <AccountBalanceWalletRoundedIcon />,
        },
      ],
    },
    {
      heading: "Settings",
      items: [
        { label: "Settings", path: "/pawn/settings", icon: <SettingsIcon /> },
      ],
    },
    // Not a route — `action` items run in place instead of navigating.
    ...(switchableStores.length > 0 || canSwitchBranches
      ? [
          {
            heading: "Stores",
            items: [
              ...(switchableStores.length > 0
                ? [
                    {
                      label: "Switch Store",
                      action: () => setSwitcherOpen(true),
                      icon: <SyncRoundedIcon />,
                    },
                  ]
                : []),
              ...(canSwitchBranches
                ? [
                    {
                      label: "Switch Branch",
                      action: () => setBranchSwitcherOpen(true),
                      icon: <PlaceRoundedIcon />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];

  // Flat list for the collapsed icon rail.
  const menuItems = menuSections.flatMap((section) => section.items);

  // Navigating always collapses the sidebar, whichever mechanism opened it. The
  // hover flag has to be cleared explicitly: the element under the cursor is
  // replaced by the incoming route, so `mouseleave` frequently never fires and
  // the rail would stay expanded on top of the page it just navigated to.
  const closeSidebar = useCallback(() => {
    setIsHovered(false);
    setRailOpen(false);
    setDrawerOpen(false);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    closeSidebar();
  };

  // Menu entries are either a route or an in-place action (the store switcher);
  // both collapse the sidebar so the dialog isn't opened behind an expanded rail.
  const handleMenuItem = (item) => {
    closeSidebar();
    if (item.action) item.action();
    else navigate(item.path);
  };

  const toggleDrawer = () => {
    setDrawerOpen((v) => !v);
  };

  /* ── Pawn-only guard ────────────────────────────────────────────────────────
   * Every screen under this layout belongs to is_pawn stores alone, and the check
   * has to live here rather than at the switcher because browser history outlives
   * a store switch: someone who browsed a pawn store, went back to Daily Tick and
   * switched to a plain store still has /pawn/* entries behind them, and the back
   * button would otherwise walk straight into these screens with a store that has
   * no pawn data at all. `replace` so the offending entry is consumed rather than
   * stacked, which would make back bounce between the two.
   *
   * Only a KNOWN non-pawn store is turned away — an empty store record means
   * storage hasn't been written yet, not that the store fails the rule. */
  const storeKnown = currentStoreInfo?.id != null;
  if (storeKnown && !isPawnStore(currentStoreInfo)) {
    return <Navigate to="/home" replace />;
  }

  const drawerContent = (
    <Box
      sx={{
        // Fill whatever drawer paper hosts this (260px on desktop, 280px on
        // mobile). A fixed 260px here left a stray vertical border + gap inside
        // the wider mobile paper. The paper supplies its own borderRight.
        width: "100%",
        height: "100%",
        background: SIDEBAR_BG,
        color: INK,
        // Column layout rather than a static header + absolutely-positioned
        // footer: the nav list is ~640px tall, so on a landscape tablet (1024
        // ×600) the footer used to sit on top of the last menu items with no way
        // to scroll to them. Now the list is the only part that scrolls.
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 12px",
          borderBottom: `1px solid ${BORDER}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flex: 1,
          }}
        >
          {/* Also a mode-gesture target (see holdProps) — the logo tile is the
              one element present in every sidebar state. */}
          <Box
            onClick={handleLogoClick}
            {...holdProps}
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 6px rgba(37,99,235,0.25)",
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <StoreIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                color: INK,
                maxWidth: "100%",
              }}
            >
              {currentStoreInfo.name ? `${currentStoreInfo.name}` : ""}
            </Typography>
            <Typography
              variant="caption"
              title={currentStoreInfo.address || ""}
              sx={{
                color: INK_MUTED,
                fontSize: "0.6rem",
                lineHeight: 1.3,
                width: "100%",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
                overflowWrap: "anywhere",
              }}
            >
              {currentStoreInfo.address ? `${currentStoreInfo.address}` : ""}
            </Typography>
          </Box>
        </Box>

        {/* Explicit collapse control wherever hover can't do it (phone drawer
            and the tap-expanded rail on touch tablets). */}
        {!canHover && (
          <IconButton
            onClick={closeSidebar}
            size="small"
            sx={{ color: INK_MUTED, flexShrink: 0, ml: 0.5 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <List
        sx={{
          px: 1,
          py: 1,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {menuSections.map((section) => (
          <React.Fragment key={section.heading}>
            <Typography
              noWrap
              sx={{
                px: 1.5,
                pt: 1.5,
                pb: 0.5,
                fontSize: "0.6rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: INK_FAINT,
              }}
            >
              {section.heading}
            </Typography>
            {section.items.map((item) => {
              const isActive = !!item.path && location.pathname === item.path;
              return (
                <ListItem
                  key={item.path || item.label}
                  onClick={() => handleMenuItem(item)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    cursor: "pointer",
                    backgroundColor: isActive ? BLUE_PALE : "transparent",
                    borderRight: isActive
                      ? `3px solid ${BLUE}`
                      : "3px solid transparent",
                    "&:hover": {
                      backgroundColor: isActive ? BLUE_PALE : "#EEF2F6",
                    },
                    py: 0.75,
                  }}
                >
                  <ListItemIcon
                    sx={{ color: isActive ? BLUE : INK_MUTED, minWidth: 36 }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      noWrap: true,
                      fontSize: "0.8rem",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? BLUE_INK : INK_SOFT,
                    }}
                  />
                </ListItem>
              );
            })}
          </React.Fragment>
        ))}
      </List>

      {/* Pinned footer: leave the module (Daily Tick) or leave the session
          (Log out). Both are exits, so they share one row and neither can be
          scrolled off by a long menu. */}
      <Box
        sx={{
          m: 2,
          mt: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          // Fixed height + clipped overflow so the footer never changes size or
          // wraps while the sidebar animates open (70px → 260px).
          height: 52,
          flexShrink: 0,
          overflow: "hidden",
          borderTop: `1px solid ${BORDER}`,
          pt: 1,
        }}
      >
        <Box
          onClick={() => handleNavigation("/home")}
          sx={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            px: 1.5,
            cursor: "pointer",
            borderRadius: 1.5,
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: BLUE_PALE,
            },
          }}
        >
          <DailyTickIcon
            style={{
              width: 28,
              height: 28,
              flexShrink: 0,
            }}
          />

          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              color: INK_SOFT,
              userSelect: "none",
              // Always one row — never break into "Daily" / "Tick".
              whiteSpace: "nowrap",
            }}
          >
            Daily Tick
          </Typography>
        </Box>

        <Tooltip title="Log out" placement="top" arrow>
          <IconButton
            onClick={() => {
              closeSidebar();
              setLogoutOpen(true);
            }}
            sx={{
              flexShrink: 0,
              color: "#DC2626",
              borderRadius: 1.5,
              "&:hover": { backgroundColor: "rgba(220,38,38,0.08)" },
            }}
          >
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box
      className="pawn-module"
      sx={{
        display: "flex",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        margin: 0,
        padding: 0,
        position: "relative",
        background: PAGE_BG,
      }}
    >
      {/* App Bar for Mobile */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            background: PAGE_BG,
            zIndex: theme.zIndex.drawer + 1,
            boxShadow: "none",
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Toolbar sx={{ minHeight: "56px !important", padding: "0 12px" }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleMenuClick}
              {...holdProps}
              size="small"
              sx={{ color: INK_MUTED, touchAction: "manipulation" }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{ flexGrow: 1, fontSize: "1rem", ml: 1, color: INK }}
            >
              Pawn Shop
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Persistent rail (tablet + desktop). Expanded by hover on a mouse, by
          the rail's own menu button on touch — where a backdrop below makes
          "tap outside to close" work. */}
      {!isMobile && isExpanded && !canHover && (
        <Box
          onClick={() => setRailOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1299,
            bgcolor: "rgba(15,23,42,0.32)",
          }}
        />
      )}
      {!isMobile && (
        <Box
          onMouseEnter={canHover ? () => setIsHovered(true) : undefined}
          onMouseLeave={canHover ? () => setIsHovered(false) : undefined}
          sx={{
            position: "fixed",
            left: 0,
            top: 0,
            height: "100vh",
            zIndex: 1300,
            width: isExpanded ? 260 : 70,
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <Drawer
            variant="permanent"
            anchor="left"
            open={true}
            sx={{
              width: isExpanded ? 260 : 70,
              "& .MuiDrawer-paper": {
                width: isExpanded ? 260 : 70,
                boxSizing: "border-box",
                transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                overflowX: "hidden",
                border: "none",
                background: SIDEBAR_BG,
                color: INK,
                position: "absolute",
                height: "100vh",
                boxShadow: isExpanded
                  ? "4px 0 24px rgba(15,23,42,0.08)"
                  : "none",
                borderRight: `1px solid ${BORDER}`,
              },
            }}
          >
            {isExpanded ? (
              drawerContent
            ) : (
              // Collapsed view - icons at top
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                  height: "100%",
                  // Same reason as the expanded drawer: the icon column is
                  // taller than a landscape tablet, so it has to be the part
                  // that scrolls rather than run under the Daily Tick footer.
                  overflow: "hidden",
                }}
              >
                {/* Logo at top — also the mode-gesture target (see holdProps). */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                    py: 2,
                    mb: 1,
                    flexShrink: 0,
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  <Box
                    onClick={handleLogoClick}
                    {...holdProps}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 6px rgba(37,99,235,0.25)",
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                    }}
                  >
                    <StoreIcon sx={{ fontSize: 22, color: "#fff" }} />
                  </Box>

                  {/* Touch tablets have no hover to expand the rail with and no
                      AppBar to put a hamburger in, so the rail carries its own. */}
                  {!canHover && (
                    <Tooltip title="Menu" placement="right" arrow>
                      <IconButton
                        onClick={() => setRailOpen(true)}
                        size="small"
                        sx={{ color: INK_MUTED }}
                      >
                        <MenuIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {/* Navigation Icons - Top aligned */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                    width: "100%",
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {menuItems.map((item) => (
                    <Tooltip
                      key={item.path || item.label}
                      title={item.label}
                      placement="right"
                      arrow
                    >
                      <IconButton
                        onClick={() => handleMenuItem(item)}
                        sx={{
                          color:
                            location.pathname === item.path ? BLUE : INK_MUTED,
                          backgroundColor:
                            location.pathname === item.path
                              ? BLUE_PALE
                              : "transparent",
                          borderRadius: 2,
                          width: 44,
                          height: 44,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          "&:hover": {
                            backgroundColor: BLUE_PALE,
                            transform: "scale(1.05)",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        {item.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                </Box>

                {/* Pinned exits — Daily Tick, then Log out (see the expanded
                    drawer's footer for why they sit together). */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                    width: "100%",
                    py: 1.5,
                    flexShrink: 0,
                    borderTop: `1px solid ${BORDER}`,
                  }}
                >
                  <Tooltip title="Daily Tick" placement="right" arrow>
                    <IconButton
                      onClick={() => handleNavigation("/home")}
                      sx={{
                        color: INK_SOFT,
                        p: 0.5,
                        "&:hover": {
                          transform: "scale(1.1)",
                          backgroundColor: BLUE_PALE,
                        },
                      }}
                    >
                      <DailyTickIcon
                        style={{
                          width: 28,
                          height: 28,
                        }}
                      />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Log out" placement="right" arrow>
                    <IconButton
                      onClick={() => {
                        closeSidebar();
                        setLogoutOpen(true);
                      }}
                      sx={{
                        color: "#DC2626",
                        p: 0.75,
                        "&:hover": {
                          transform: "scale(1.1)",
                          backgroundColor: "rgba(220,38,38,0.08)",
                        },
                      }}
                    >
                      <LogoutIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Drawer>
        </Box>
      )}

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen && isMobile}
        onClose={() => setDrawerOpen(false)}
        sx={{
          // Sit above the fixed AppBar (drawer + 1) so the drawer's own header
          // is not hidden behind it when open.
          zIndex: (t) => t.zIndex.drawer + 2,
          "& .MuiDrawer-paper": {
            width: 280,
            background: SIDEBAR_BG,
            borderRight: `1px solid ${BORDER}`,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content - With proper padding */}
      <Box
        component="main"
        className="pawn-layout-main"
        sx={{
          flexGrow: 1,
          // On mobile the fixed 56px AppBar pushes this Box down via mt; its
          // height must be reduced by the same amount or the bottom 56px would
          // fall outside the viewport and get clipped (parent is overflow:hidden).
          height: isMobile ? "calc(100vh - 56px)" : "100vh",
          overflow: "auto",
          // Without this a wide child (a report table, a ticket preview) sizes
          // the flex item itself and drags the whole page sideways instead of
          // scrolling inside its own container.
          minWidth: 0,
          mt: isMobile ? "56px" : 0,
          ml: !isMobile ? "70px" : 0,
          p: { xs: 0, sm: 0, md: 0 },
          transition: "margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          background: SIDEBAR_BG,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#F1F5F9",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "3px",
            "&:hover": {
              background: "#94A3B8",
            },
          },
        }}
      >
        <Outlet />
      </Box>

      {/* Connected-store picker, opened from the rail's Switch Store entry.
          Pawn stores only — see the switcher block at the top of this file. */}
      <StoreSwitcherDialog
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        stores={switchableStores}
        loading={switchableLoading}
        note="Pawn stores only. Other stores are reachable from the Daily Tick sidebar."
      />

      {/* Branch picker, opened from the rail's Switch Branch entry. */}
      <BranchSwitcherDialog
        open={branchSwitcherOpen}
        onClose={() => setBranchSwitcherOpen(false)}
        branches={switchableBranches}
        loading={branchesLoading}
        canViewAll={canViewAllBranches}
        viewingAll={viewingAllBranches}
      />

      {/* Sign out, opened from either footer (expanded drawer / collapsed rail) */}
      <LogoutConfirmDialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
      />

      {/* Password prompt for the press-and-hold mode gesture */}
      <Dialog
        open={pwOpen}
        onClose={() => setPwOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700 }}>
          Enter Password
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            type="password"
            size="small"
            value={pwValue}
            error={pwError}
            helperText={pwError ? "Incorrect password" : " "}
            onChange={(e) => {
              setPwValue(e.target.value);
              if (pwError) setPwError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitPassword();
              }
            }}
            inputProps={{
              autoCapitalize: "none",
              autoCorrect: "off",
              autoComplete: "off",
              spellCheck: false,
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPwOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitPassword}
            sx={{ textTransform: "none" }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PawnLayout;
